import React from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import styled from '@emotion/styled';
import VocabSentences from 'components/vocab/vocab_sentences';
import VocabLabels from 'components/vocab/vocab_labels';
import VocabWordInfoList from 'components/vocab/vocab_word_info_list';
import { VocabWordWrapper } from '@seedlang/hoc';
import VocabWordConcepts from 'components/vocab/vocab_word_concepts';
import RelatedWords from 'components/vocab/related_words';
import WordCardConjugationRow from 'components/word_card/word_card_conjugation_row';
import { AppUI } from '@seedlang/state';
import Link from 'components/link';
import LearnedButton from 'components/review/learned_button';
import ReviewToggle from 'components/review/review_toggle';
import Spinner from 'components/spinner';
import Text from 'components/text';
import VideoPlayerWithSupplement from 'components/media/video_player_with_supplement';

const Wrapper = styled.div`
  padding: 10px 10px 30px 10px;
`;

const VocabInfo = styled.div`
`;

const AdminLink = styled.div`
  font-size: 11px;
  text-decoration: underline;
  text-align: center;
  padding: 10px 5px 0 5px;
`;

const LearnedWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const VideoPlayerWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${props => props.minHeight};
`;

const EmptyVideo = styled.div`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: #CCC;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const RelatedWordsWrapper = styled.div`
  margin-top: 20px;
`;

@observer
class VocabCard extends React.Component {
  @observable showVocabSentences = false;

  constructor(props) {
    super(props);
    setTimeout(() => this.showVocabSentences = true, 300);
  }

  render() {
    const videoWidth = AppUI.layout.isMobile ? 150 : 180;
    return (
      <Wrapper>
        <VocabInfo>
          <VocabLabels
            flexDirection='row'
            position='static'
            word={this.props.word}
          />
          {
            Boolean(this.props.word) &&
            <VideoPlayerWrapper
              minHeight={`${videoWidth}px`}
            >
              {
                this.props.showSpinner && !this.props.shouldShowVocabVideo &&
                <Spinner
                  margin='0'
                />
              }
              {
                this.props.shouldShowVocabVideo &&
                <VideoPlayerWithSupplement
                  width={videoWidth}
                  hasVideoClip
                  autoPlay={this.props.autoPlay}
                  videoClip={this.props.vocabCardVideoClip}
                  supplementVideoClip={this.props.supplementVideoClip}
                />
              }
              {
                !this.props.showSpinner && !this.props.shouldShowVocabVideo &&
                <EmptyVideo
                  size={videoWidth}
                >
                  <Text
                    center
                    fontSize='13px'
                    lineHeight='16px'
                    color='#333'
                  >
                    We will record a video for this word soon!
                  </Text>
                </EmptyVideo>
              }
            </VideoPlayerWrapper>
          }
          <LearnedWrapper>
            {
              this.props.showVocabCardReviewToggle &&
              <ReviewToggle
                interfaceSource='vocab-card'
                buttonText='Add to Reviews'
                word={this.props.reviewWord}
              />
            }
            <LearnedButton
              word={this.props.word}
            />
          </LearnedWrapper>
          <VocabWordInfoList
            {...this.props}
            word={this.props.word}
            hideRelatedWords
            hideSynonyms
            rowMargin='5px 0'
          />
          {
            this.props.shouldShowConjugationRow &&
            <WordCardConjugationRow
              id={`conjugation-${this.props.word.id}`}
              word={this.props.word}
              expandedId={this.props.expandedId}
              onClickRow={this.props.onClickRow}
              rowMargin='5px 0'
              displayNameLine1WithCase='Conjugation'
              displayNameLine2={this.props.word.conjugatedVerbStringsSummary}
              conjugationTableProps={{
                hideHeader: true,
                expand: true,
                conjugatedVerbStrings: this.props.word.conjugatedVerbStrings,
                padding: '0',
              }}
            />
          }
          {
            this.props.shouldShowConceptRows &&
              <VocabWordConcepts
                word={this.props.word}
                expandedId={this.props.expandedId}
                onClickRow={this.props.onClickRow}
                rowMargin='10px 0'
              />
          }
          {
            (this.props.shouldShowSynonyms || this.props.shouldShowRelatedWords) &&
            <RelatedWordsWrapper>
              {
                this.props.shouldShowSynonyms &&
                <RelatedWords
                  title='Synonyms'
                  referenceWord={this.props.word}
                  words={this.props.word.publicSynonyms}
                  onClickWord={this.props.onClickWord}
                />
              }
              {
                this.props.shouldShowRelatedWords &&
                <RelatedWords
                  title='Related Words'
                  referenceWord={this.props.word}
                  words={this.props.relatedWords}
                  onClickWord={this.props.onClickWord}
                />
              }
            </RelatedWordsWrapper>
          }
          {
            AppUI.user.admin && this.props.word && this.props.word.id &&
            <AdminLink>
              <Link
                to={{name: 'builder.words.edit', params: {wordId: this.props.word.id}}}
                target='_blank'
              >
                Admin Link
              </Link>
            </AdminLink>
          }
        </VocabInfo>
        {
          this.showVocabSentences &&
            <VocabSentences
              inDeck
              wordSentenceStore={this.props.wordSentenceStore}
              wordId={this.props.word.id}
              showSentenceSubmissions
              expandSentenceSubmissionCreate
              showHeader
              hideWhenBlank={this.props.isShowingMeanings}
            />
        }
      </Wrapper>
    );
  }
}

export default VocabWordWrapper(VocabCard);
